import { logError } from './logger'

/**
 * 应用侧的 Service Worker 清理兜底。
 *
 * 主力方案是 public/service-worker.js 那份自毁脚本（它负责解救「被旧缓存困住、
 * 根本加载不到新代码」的用户）。这里是第二条路：只要用户成功加载到了新版本代码，
 * 就顺手把残留的 SW 注册与 Cache Storage 清干净，避免旧缓存继续拦截导航请求
 * 导致 nginx 的重定向失效。
 *
 * 作用域约束（重要）：
 * navigator.serviceWorker.getRegistrations() 与 caches.keys() 都是**源级**接口，
 * 返回的是整个 origin 下的内容，不限于本应用路径。而同一个域名下还部署着别的应用
 * （如 /solosmart-ai-h5/ 移动端分享站、/solomarketing-videoCut/ 视频剪辑站），
 * 无差别清空会把它们的离线能力和缓存资产一起删掉，且事后极难定位到是本应用干的。
 * 因此这里只处理 scope 落在本应用 publicPath 之下的注册，以及名字里带本应用 scope 的缓存
 * （workbox 生成的 precache 名称形如 `workbox-precache-v2-<scope>`，天然带 scope）。
 *
 * @date 2026-09-01
 * @ai-generated true
 * @ai-tool Claude Code
 */

/** 本应用的 SW 作用域绝对地址，如 https://host/cn/ */
const appScope = () => new URL(process.env.BASE_URL || '/', window.location.origin).href

export const cleanupServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return
  try {
    const scope = appScope()

    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(
      registrations
        // 只注销本应用作用域下的注册，同源其他应用的 SW 一律不动
        .filter((registration) => registration.scope.startsWith(scope))
        .map((registration) => registration.unregister())
    )

    if (window.caches) {
      const keys = await caches.keys()
      await Promise.all(keys.filter((key) => key.includes(scope)).map((key) => caches.delete(key)))
    }
  } catch (error) {
    // 清理失败不影响主流程：自毁 SW 在下次访问时仍会再试一次
    logError('serviceWorkerCleanup', error)
  }
}
